import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { salesReportGroup } from "../lib/serviceTypeNames";
import { ChevronLeft, ChevronRight, RefreshCw, Search, Wallet, X } from "lucide-react";

// One table for every income line of a month: project payments, support fees
// and CRM retainers. Totals come from GET /income/consolidated, which follows
// /stats/owner-dashboard exactly — the PLAN here is the dashboard's PLAN.

const FULL_MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const SOURCES = {
  sales: { label: "Projects", cls: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  support: { label: "Support", cls: "bg-violet-500/10 text-violet-400 border-violet-500/30" },
  crm: { label: "CRM", cls: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30" },
};

const STATES = {
  bad_debt: { label: "Bad debt", cls: "bg-red-500/10 text-red-400 border-red-500/30" },
  invoiced: { label: "Invoiced", cls: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  planned: { label: "Not invoiced", cls: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30" },
  // CRM retainer not generated yet on /crm-income — projected from the client's tariff.
  projected: { label: "Projected", cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30 border-dashed" },
  paid: { label: "Paid", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  paid_other: { label: "Paid", cls: "bg-emerald-500/10 text-emerald-400/70 border-emerald-500/20" },
  low_probability: { label: "< 50%", cls: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20" },
};

const STATE_FILTERS = [
  { key: "all", label: "All" },
  { key: "open", label: "To collect", match: (r) => r.in_plan && ["invoiced", "planned", "bad_debt", "projected"].includes(r.state) },
  { key: "paid", label: "Paid", match: (r) => r.state === "paid" || r.state === "paid_other" },
  { key: "low", label: "Below 50%", match: (r) => !r.in_plan && !r.in_fact },
];

// Segments for the breakdown panels and table grouping. Departments are the
// three teams that bring money in — Sales (project payments), Support (monthly
// support fees) and CRM (retainers) — each with its own responsible managers.
// Product follows the Sales P&L rule (Spinhouse is one product).
const DEPARTMENT_ORDER = ["Sales", "Support", "CRM"];
const DIMENSIONS = {
  department: { label: "Departments", key: (r) => r.department || "Other" },
  manager: { label: "Managers", key: (r) => r.manager_name || "Not assigned" },
  product: {
    label: "Products",
    key: (r) => r.source === "crm" ? "CRM subscription"
      : r.source === "support" ? "Support"
      : salesReportGroup(r.service_type) || "Other",
  },
};

// A panel/table pick: {dim, key} plus, for a manager picked inside a
// department, that department — a manager can work in two of them.
const matchesSegment = (r, seg) => DIMENSIONS[seg.dim].key(r) === seg.key && (!seg.dept || r.department === seg.dept);

// plan = collected + toCollect; outOfPlan = booked under 50%; extra = cash
// that arrived this month for invoices booked in another month; cashIn = all
// money that arrived this month (collected + extra). cashIn is what the owner
// counts as "paid in October" — collected alone hides late invoices.
const segmentize = (rows, keyFn) => {
  const map = new Map();
  rows.forEach((r) => {
    const k = keyFn(r);
    const g = map.get(k) || { key: k, plan: 0, cashIn: 0, collected: 0, toCollect: 0, outOfPlan: 0, extra: 0, count: 0, partner: false, rows: [] };
    g.count += 1;
    g.rows.push(r);
    g.cashIn += r.fact_usd;
    if (r.is_partner) g.partner = true;
    if (r.in_plan) {
      g.plan += r.amount_usd;
      if (r.state === "paid" || r.state === "paid_other") g.collected += r.amount_usd;
      else g.toCollect += r.amount_usd;
    } else if (r.in_fact) {
      g.extra += r.fact_usd;
    } else {
      g.outOfPlan += r.amount_usd;
    }
    map.set(k, g);
  });
  return [...map.values()].sort((a, b) => (b.plan + b.outOfPlan + b.extra) - (a.plan + a.outOfPlan + a.extra));
};

const segWeight = (g) => g.plan + g.outOfPlan + g.extra;

const PanelHeader = ({ title }) => (
  <div className="flex items-baseline justify-between mb-2">
    <span className="text-sm font-medium text-zinc-200">{title}</span>
    <span className="flex text-[10px] uppercase tracking-wide text-zinc-500 font-mono">
      <span className="w-16 text-right" title="Booked for this month, probability 50%+">plan</span>
      <span className="w-16 text-right" title="All money that arrived this month, including invoices booked for earlier months">paid</span>
      <span className="w-16 text-right" title="Plan not paid yet">left</span>
    </span>
  </div>
);

const SegRow = ({ g, max, active, onClick, strong, indent, partnerTag }) => {
  const pct = g.plan ? Math.round((g.collected / g.plan) * 100) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("w-full text-left rounded px-1.5 py-1 transition-colors",
        indent && "pl-4",
        active ? "bg-zinc-800 ring-1 ring-zinc-600" : "hover:bg-zinc-800/60")}
      title={`Plan ${usd(g.plan)}: paid ${usd(g.collected)}${pct === null ? "" : ` (${pct}%)`}, left ${usd(g.toCollect)}` +
        (g.outOfPlan ? ` · under 50%: ${usd(g.outOfPlan)}` : "") +
        (g.extra ? ` · paid now for earlier months: ${usd(g.extra)}` : "") + ` · ${g.count} rows`}
    >
      <div className={cn("flex items-center justify-between gap-2", strong ? "text-sm" : "text-xs")}>
        <span className={cn("truncate", strong ? "font-medium text-zinc-100" : "text-zinc-300")}>
          {g.key}
          {partnerTag && g.partner && <span className="ml-1 text-[10px] text-orange-400">partner</span>}
        </span>
        <span className={cn("flex shrink-0 font-mono whitespace-nowrap", strong ? "text-xs font-semibold" : "")}>
          <span className="w-16 text-right text-zinc-200">{usd(g.plan)}</span>
          <span className={cn("w-16 text-right", g.cashIn ? "text-emerald-400" : "text-zinc-600")}>{usd(g.cashIn)}</span>
          <span className={cn("w-16 text-right", g.toCollect ? "text-amber-400" : "text-zinc-600")}>{usd(g.toCollect)}</span>
        </span>
      </div>
      <div className={cn("mt-1 flex w-full overflow-hidden rounded-full bg-zinc-800/60", strong ? "h-2" : "h-1.5")}>
        <div className="bg-emerald-500" style={{ width: `${(g.collected / max) * 100}%` }} />
        <div className="bg-amber-500/80" style={{ width: `${(g.toCollect / max) * 100}%` }} />
        <div className="bg-zinc-500/40" style={{ width: `${(g.outOfPlan / max) * 100}%` }} />
        <div className="bg-sky-500/50" style={{ width: `${(g.extra / max) * 100}%` }} />
      </div>
    </button>
  );
};

// Each department with its managers underneath, one scale for both so a
// manager's bar reads as a part of the department's.
const DepartmentPanel = ({ rows, active, onPick }) => {
  const depts = useMemo(() => segmentize(rows, DIMENSIONS.department.key)
    .sort((a, b) => DEPARTMENT_ORDER.indexOf(a.key) - DEPARTMENT_ORDER.indexOf(b.key)), [rows]);
  const max = Math.max(1, ...depts.map(segWeight));
  const isActive = (seg) => active && active.dim === seg.dim && active.key === seg.key && (active.dept || null) === (seg.dept || null);
  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="income-panel-department">
      <CardContent className="p-3">
        <PanelHeader title="Departments and managers" />
        <div className="space-y-2.5">
          {depts.map((d) => {
            const deptSeg = { dim: "department", key: d.key };
            return (
              <div key={d.key}>
                <SegRow g={d} max={max} strong active={isActive(deptSeg)} onClick={() => onPick(isActive(deptSeg) ? null : deptSeg)} />
                <div className="mt-0.5 space-y-0.5">
                  {segmentize(d.rows, DIMENSIONS.manager.key).map((m) => {
                    const mSeg = { dim: "manager", key: m.key, dept: d.key };
                    return <SegRow key={m.key} g={m} max={max} indent partnerTag active={isActive(mSeg)} onClick={() => onPick(isActive(mSeg) ? null : mSeg)} />;
                  })}
                </div>
              </div>
            );
          })}
          {depts.length === 0 && <div className="text-xs text-zinc-500 py-2">Nothing here</div>}
        </div>
      </CardContent>
    </Card>
  );
};

const ProductPanel = ({ rows, active, onPick }) => {
  const [expanded, setExpanded] = useState(false);
  const segs = useMemo(() => segmentize(rows, DIMENSIONS.product.key), [rows]);
  const max = Math.max(1, ...segs.map(segWeight));
  const visible = expanded ? segs : segs.slice(0, 10);
  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-testid="income-panel-product">
      <CardContent className="p-3">
        <PanelHeader title="Products" />
        <div className="space-y-1">
          {visible.map((g) => {
            const on = active?.dim === "product" && active?.key === g.key;
            return <SegRow key={g.key} g={g} max={max} active={on} onClick={() => onPick(on ? null : { dim: "product", key: g.key })} />;
          })}
          {segs.length === 0 && <div className="text-xs text-zinc-500 py-2">Nothing here</div>}
        </div>
        {segs.length > 10 && (
          <button type="button" onClick={() => setExpanded(!expanded)} className="mt-1 text-[11px] text-zinc-500 hover:text-zinc-300">
            {expanded ? "Show less" : `+${segs.length - 10} more`}
          </button>
        )}
      </CardContent>
    </Card>
  );
};

const CUR_SIGN = { USD: "$", EUR: "€", UAH: "₴" };
const usd = (v) => `$${Math.round(v || 0).toLocaleString()}`;
const money = (v, cur) => `${CUR_SIGN[cur] || ""}${Math.round(v || 0).toLocaleString()}${CUR_SIGN[cur] ? "" : ` ${cur}`}`;

const rowLink = (r) => {
  if (r.source === "sales" && r.project_id) return `/sales/project/${r.project_id}`;
  if (r.source === "support") return "/support-payments";
  if (r.source === "crm") return "/crm-income";
  return null;
};

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={cn(
      "px-2.5 py-1 rounded-md text-xs border transition-colors",
      active ? "bg-zinc-700 border-zinc-600 text-zinc-50" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"
    )}
  >
    {children}
  </button>
);

const IncomePage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [segment, setSegment] = useState(null); // {dim, key} picked on a panel
  const [groupBy, setGroupBy] = useState("department");

  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/income/consolidated", { params: { month: monthKey } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load income");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [monthKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftMonth = (d) => {
    const m = month + d;
    if (m < 1) { setMonth(12); setYear(year - 1); }
    else if (m > 12) { setMonth(1); setYear(year + 1); }
    else setMonth(m);
  };

  // Panels see every filter except the segment pick itself, so picking a
  // manager does not collapse the managers panel to one bar.
  const baseRows = useMemo(() => {
    const st = STATE_FILTERS.find((f) => f.key === stateFilter);
    const q = query.trim().toLowerCase();
    return (data?.rows || []).filter((r) =>
      (source === "all" || r.source === source) &&
      (!st?.match || st.match(r)) &&
      (!q || [r.client_name, r.title, r.project_name, r.manager_name, r.service_type].some((v) => (v || "").toLowerCase().includes(q)))
    );
  }, [data, source, stateFilter, query]);

  // A pick on one panel narrows the other one, never itself.
  const deptPanelRows = segment?.dim === "product" ? baseRows.filter((r) => matchesSegment(r, segment)) : baseRows;
  const productPanelRows = segment && segment.dim !== "product" ? baseRows.filter((r) => matchesSegment(r, segment)) : baseRows;

  const rows = useMemo(() => (segment
    ? baseRows.filter((r) => matchesSegment(r, segment))
    : baseRows), [baseRows, segment]);

  const groups = useMemo(() => (groupBy === "none"
    ? [{ key: null, rows, plan: 0 }]
    : groupBy === "department"
      ? segmentize(rows, DIMENSIONS.department.key).sort((a, b) => DEPARTMENT_ORDER.indexOf(a.key) - DEPARTMENT_ORDER.indexOf(b.key))
      : segmentize(rows, DIMENSIONS[groupBy].key)), [rows, groupBy]);

  const shown = useMemo(() => rows.reduce((acc, r) => {
    if (r.in_plan) acc.plan += r.amount_usd;
    acc.fact += r.fact_usd;
    return acc;
  }, { plan: 0, fact: 0 }), [rows]);

  const t = data?.totals;
  const collectedPct = t?.plan_usd ? Math.round((t.collected_usd / t.plan_usd) * 100) : 0;
  const factOutsidePlan = t ? t.fact_usd - t.collected_usd : 0;

  return (
    <div className="space-y-4" data-testid="income-consolidated-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-emerald-400" />
          <h1 className="text-xl font-semibold text-zinc-50">Income</h1>
          <span className="text-sm text-zinc-500">all streams, one month</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="w-36 text-center text-sm font-medium text-zinc-200">{FULL_MONTH_NAMES[month - 1]} {year}</span>
          <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={load} disabled={loading}><RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /></Button>
        </div>
      </div>

      {t && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-end gap-x-8 gap-y-3">
              <div title="Booked for this month with probability 50%+ — same number as the owner dashboard">
                <div className="text-xs text-zinc-500">Plan</div>
                <div className="text-2xl font-bold text-zinc-50" data-testid="income-plan-total">{usd(t.plan_usd)}</div>
                <div className="text-[11px] text-zinc-500">&nbsp;</div>
              </div>
              <div title="All money that arrived this month, including invoices booked for earlier months">
                <div className="text-xs text-zinc-500">Paid this month</div>
                <div className="text-2xl font-bold text-emerald-400" data-testid="income-paid-total">{usd(t.fact_usd)}</div>
                <div className="text-[11px] text-zinc-500">
                  {usd(t.collected_usd)} of plan ({collectedPct}%)
                  {factOutsidePlan > 1 && <> + {usd(factOutsidePlan)} for earlier months</>}
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-500">Left to collect</div>
                <div className="text-2xl font-bold text-amber-400">{usd(t.outstanding_usd)}</div>
                <div className="text-[11px] text-zinc-500">&nbsp;</div>
              </div>
              {data.below_threshold_usd > 0 && (
                <div title="Booked for this month with probability under 50% — not counted in the plan">
                  <div className="text-xs text-zinc-500">Below 50%, not in plan</div>
                  <div className="text-lg font-semibold text-zinc-500">{usd(data.below_threshold_usd)}</div>
                  <div className="text-[11px] text-zinc-500">&nbsp;</div>
                </div>
              )}
            </div>
            <div className="h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${Math.min(collectedPct, 100)}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-400">
              {Object.entries(SOURCES).map(([k, s]) => (
                <span key={k}>
                  <span className={cn("inline-block px-1.5 rounded border mr-1.5", s.cls)}>{s.label}</span>
                  plan {usd(data.by_source[k].plan_usd)} · paid {usd(data.by_source[k].fact_usd)}
                </span>
              ))}
              {(data.crm_projected_count || 0) > 0 && (
                <Link to="/crm-income" className="text-cyan-300/80 hover:text-cyan-200" data-testid="income-crm-projected"
                  title="This month's CRM payments are not generated yet — the plan uses each client's tariff. Generate the month on CRM Payments to turn them into real invoices.">
                  CRM: {data.crm_projected_count} projected from tariffs, month not generated →
                </Link>
              )}
              {Object.keys(data.plan_by_currency || {}).length > 1 && (
                <span className="text-zinc-500">
                  plan in original currencies: {Object.entries(data.plan_by_currency).map(([c, v]) => money(v, c)).join(" + ")}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {data && (
        <div className="space-y-1.5">
          <div className="grid gap-3 lg:grid-cols-2">
            <DepartmentPanel rows={deptPanelRows} active={segment} onPick={setSegment} />
            <ProductPanel rows={productPanelRows} active={segment} onPick={setSegment} />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-zinc-500">
            <span><span className="inline-block h-2 w-2 rounded-sm bg-emerald-500 mr-1" />paid from plan</span>
            <span><span className="inline-block h-2 w-2 rounded-sm bg-amber-500/80 mr-1" />left to collect</span>
            <span><span className="inline-block h-2 w-2 rounded-sm bg-zinc-500/40 mr-1" />under 50%, not in plan</span>
            <span><span className="inline-block h-2 w-2 rounded-sm bg-sky-500/50 mr-1" />paid now for earlier months</span>
            <span>· Click a row to filter the table.</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {segment && (
          <button type="button" onClick={() => setSegment(null)} data-testid="income-segment-clear"
            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-orange-500/40 bg-orange-500/10 text-orange-400">
            {DIMENSIONS[segment.dim].label.replace(/s$/, "")}: {segment.dept ? `${segment.dept} · ` : ""}{segment.key}<X className="h-3 w-3" />
          </button>
        )}
        <Chip active={source === "all"} onClick={() => setSource("all")}>All sources</Chip>
        {Object.entries(SOURCES).map(([k, s]) => (
          <Chip key={k} active={source === k} onClick={() => setSource(k)} testId={`income-source-${k}`}>{s.label}</Chip>
        ))}
        <span className="mx-1 h-4 w-px bg-zinc-800" />
        {STATE_FILTERS.map((f) => (
          <Chip key={f.key} active={stateFilter === f.key} onClick={() => setStateFilter(f.key)}>{f.label}</Chip>
        ))}
        <span className="mx-1 h-4 w-px bg-zinc-800" />
        <span className="text-xs text-zinc-500">Group</span>
        {[["department", "Dept"], ["manager", "Manager"], ["product", "Product"], ["none", "None"]].map(([k, l]) => (
          <Chip key={k} active={groupBy === k} onClick={() => setGroupBy(k)} testId={`income-group-${k}`}>{l}</Chip>
        ))}
        <div className="relative ml-auto w-56">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Client, project, manager" className="h-8 pl-7 text-sm bg-zinc-900 border-zinc-800" />
        </div>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400">Client</TableHead>
                <TableHead className="text-zinc-400">What</TableHead>
                <TableHead className="text-zinc-400">Source</TableHead>
                <TableHead className="text-zinc-400">Manager</TableHead>
                <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                <TableHead className="text-zinc-400 text-right">USD</TableHead>
                <TableHead className="text-zinc-400 text-center">Prob</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={g.key ?? "all"}>
                  {g.key !== null && (
                    <TableRow className="border-zinc-800 bg-zinc-800/40 hover:bg-zinc-800/40" data-testid="income-group-header">
                      <TableCell colSpan={5} className="py-1.5">
                        <span className="text-sm font-medium text-zinc-100">{g.key}</span>
                        {g.partner && groupBy === "manager" && <span className="ml-1 text-[10px] text-orange-400">partner</span>}
                        <span className="ml-2 text-xs text-zinc-500">{g.count} rows</span>
                      </TableCell>
                      <TableCell className="py-1.5 text-right font-mono text-zinc-50">{usd(g.plan)}</TableCell>
                      <TableCell colSpan={2} className="py-1.5 text-xs whitespace-nowrap">
                        <span className="text-emerald-400">{usd(g.cashIn)}</span>
                        <span className="text-zinc-500"> paid · </span>
                        <span className="text-amber-400">{usd(g.toCollect)}</span>
                        <span className="text-zinc-500"> left</span>
                        {g.outOfPlan > 0 && <span className="text-zinc-500"> · {usd(g.outOfPlan)} &lt;50%</span>}
                      </TableCell>
                    </TableRow>
                  )}
              {g.rows.map((r) => {
                const link = rowLink(r);
                const st = STATES[r.state] || STATES.planned;
                const otherMonth = r.state === "paid_other" || (!r.in_plan && r.in_fact);
                const outOfPlan = !r.in_plan && !r.in_fact;
                return (
                  <TableRow key={`${r.source}-${r.id}`} className={cn("border-zinc-800", outOfPlan && "opacity-60")}>
                    <TableCell className="text-zinc-50 max-w-[220px] truncate" title={r.client_name}>{r.client_name || "—"}</TableCell>
                    <TableCell className="max-w-[320px]">
                      {link
                        ? <Link to={link} className="text-zinc-200 hover:text-zinc-50 hover:underline truncate block" title={r.title}>{r.title || r.service_type || "—"}</Link>
                        : <span className="text-zinc-200 truncate block">{r.title || "—"}</span>}
                      {r.project_name && <div className="text-xs text-zinc-500 truncate" title={r.project_name}>{r.project_name}</div>}
                    </TableCell>
                    <TableCell>
                      <span className={cn("text-xs px-1.5 py-0.5 rounded border whitespace-nowrap", SOURCES[r.source]?.cls)}>{SOURCES[r.source]?.label}</span>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm whitespace-nowrap">
                      {r.manager_name || "—"}
                      {r.is_partner && <span className="ml-1 text-[10px] text-orange-400" title="Partner (owner) sale">partner</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-zinc-300 whitespace-nowrap">{money(r.amount, r.currency)}</TableCell>
                    <TableCell className={cn("text-right font-mono whitespace-nowrap", r.in_plan ? "text-zinc-50" : "text-zinc-500")}
                      title={r.state === "projected" ? "Projected from the client's tariff — the month is not generated on CRM Payments yet" : r.in_plan ? "Counted in plan" : "Not in this month's plan"}>
                      {usd(r.in_fact && !r.in_plan ? r.fact_usd : r.amount_usd)}
                    </TableCell>
                    <TableCell className={cn("text-center text-xs", outOfPlan ? "text-zinc-500" : "text-zinc-400")}
                      title={outOfPlan ? "Under 50% — not counted in the plan" : undefined}>
                      {r.source === "crm" ? "—" : `${r.probability}%`}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className={cn("text-xs px-1.5 py-0.5 rounded border", st.cls)}>{st.label}</span>
                      {otherMonth && (
                        <span className="ml-1.5 text-[11px] font-mono text-zinc-500">
                          {r.in_plan ? `in ${r.paid_month || "?"}` : `booked ${r.booked_month || "?"}`}
                        </span>
                      )}
                      {r.state === "invoiced" && r.invoice_sent_at && (
                        <span className="ml-1.5 text-[11px] font-mono text-zinc-500">{String(r.invoice_sent_at).slice(0, 10)}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
                </React.Fragment>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-8">Nothing for this month</TableCell></TableRow>
              )}
              {rows.length > 0 && (
                <TableRow className="border-zinc-800 bg-zinc-900/60">
                  <TableCell colSpan={5} className="text-xs text-zinc-500">{rows.length} rows shown</TableCell>
                  <TableCell className="text-right font-mono text-zinc-50">{usd(shown.plan)}</TableCell>
                  <TableCell colSpan={2} className="text-xs text-zinc-500">plan · cash in {usd(shown.fact)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default IncomePage;
